import React, { useState, useEffect } from "react";
import { Grid, Typography, IconButton, FormControlLabel } from "@mui/material";
import Modal from "components/ui/Modal";
import CloseIcon from "@mui/icons-material/Close";
import Selectinput from "features/order_client_details/components/Selectinput";
import Button from "../Button";
import "./reorderSettingsModal.css";
import { styling } from "./CreateStockOrderModal";
import { Checkbox } from "@mui/material";
import dayjs from "dayjs";
import { toast } from "react-toastify";
import apiClient from "lib/api-client";
import { useNavigate } from "react-router-dom";
import { transformData, transformStockData } from "utils/transformdata";

const BUFFER_OPTIONS = ["One - 1", "Two - 2", "Three - 3", "Four - 4", "Five - 5", "Six - 6", "Seven - 7", "Eight - 8", "Nine - 9", "Ten - 10"];
const REORDER_OPTIONS = ["One - 1", "Two - 2", "Three - 3", "Four - 4", "Five - 5", "Six - 6", "Seven - 7", "Eight - 8", "Nine - 9", "Ten - 10"];
const MODE_OPTIONS = ["Generate Quote", "Generate Order", "Generate Bag"];

const getTenthValidDate = () => {
  const today = new Date();
  const cursor = new Date(today);
  cursor.setDate(today.getDate() + 1);
  let count = 0;

  while (count < 10) {
    if (cursor.getDay() !== 0) {
      count++;
    }
    if (count < 10) {
      cursor.setDate(cursor.getDate() + 1);
    }
  }

  return cursor;
};

const toOptionValue = (rawValue, options) => {
  if (rawValue === null || rawValue === undefined || rawValue === "") return "";
  const normalized = String(rawValue).trim();
  const directMatch = options.find((opt) => opt === normalized);
  if (directMatch) return directMatch;
  const byNumericSuffix = options.find((opt) => opt.split("-")[1]?.trim() === normalized);
  return byNumericSuffix || "";
};

const toQtyLabel = (value, fallbackLabel) => {
  if (!value) return fallbackLabel;
  const parts = String(value).split("-");
  return parts[1]?.trim() || parts[0]?.trim() || fallbackLabel;
};

function ReOrderSettingsModal({ open, handleClose, designData, reorderMeta = {}, onGenerateSuccess }) {
  const [checked, setChecked] = useState(false);
  const [bufferQty, setBufferQty] = useState("");
  const [reorderQty, setReorderQty] = useState("");
  const [mode, setMode] = useState("");
  const [canSave, setCanSave] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [deliveryDate, setDeliveryDate] = useState(reorderMeta?.deliveryDate || getTenthValidDate());
  const [priority, setPriority] = useState(reorderMeta?.priority || "Standard");
  const [stocks, setStocks] = useState([]);
  const [rhodium, setRhodium] = useState(reorderMeta?.rhodium || "White");
  const [reorderLocationCard, setReorderLocationCard] = useState([]);

  const [locationView, setLocationView] = useState(null); 
  const [filteredCards, setFilteredCards] = useState([]);

  const reorderPayload = designData?.reorderData;
  const baseDesign =
    reorderPayload && typeof reorderPayload === "object"
      ? { ...designData, ...reorderPayload }
      : designData || {};
  const resolvedStyleNumber = (
    baseDesign?.styleNumber ||
    designData?.styleNumber ||
    designData?.reorderData?.styleNumber ||
    ""
  )
    .toString()
    .trim();
  useEffect(() => {
    if (!open) return;
    const incomingReorder = designData?.reorderData || {};

    setBufferQty(toOptionValue(incomingReorder?.bufferStockQuantity, BUFFER_OPTIONS));
    setReorderQty(toOptionValue(incomingReorder?.reOrderQuantity, REORDER_OPTIONS));
    setMode(incomingReorder?.mode && MODE_OPTIONS.includes(incomingReorder.mode) ? incomingReorder.mode : "");
    setChecked(Boolean(incomingReorder?._id || incomingReorder?.isReorderPlace));
    setDeliveryDate(incomingReorder?.deliveryDate || reorderMeta?.deliveryDate || getTenthValidDate());
    setPriority(reorderMeta?.priority || "Standard");
    setRhodium(reorderMeta?.rhodium || "White");
  }, [open, designData?.reorderData, reorderMeta?.deliveryDate, reorderMeta?.priority, reorderMeta?.rhodium]);

  useEffect(() => {
    if (bufferQty && reorderQty && mode && checked) {
      setCanSave(true);
    } else {
      setCanSave(false);
    }
  }, [bufferQty, reorderQty, mode, checked]);

  const navigate = useNavigate();

  const clearState = () => {
    setBufferQty("");
    setReorderQty("");
    setMode("");
    setChecked(false);
    handleClose();
  };

  const handleChange = (event) => {
    setChecked(event.target.checked);
  };

  const handleGenerateQuote = async () => {

    if (!canSave || isSubmitting) return;

    const normalizeGold = (value = "") => {
      const [goldQlty = "", goldColorName = ""] = value.split(" - ");
      const colorMap = { Pink: "P", Yellow: "Y", White: "W" };

      return {
        goldQlty: goldQlty?.trim() || null,
        goldColor: colorMap[goldColorName?.trim()] || null,
      };
    };

    const prevDesign = baseDesign;
    const { goldQlty, goldColor } = normalizeGold(prevDesign?.goldQuality);
    const { goldQlty: metaGoldQlty, goldColor: metaGoldColor } = normalizeGold(reorderMeta?.goldQuality);
    const rhodiumMap = { White: "W", Pink: "P", Yellow: "Y", W: "W", P: "P", Y: "Y" };
    const goldQltyMap = { "18": "18K", "14": "14K", "18K": "18K", "14K": "14K" };
    const resolvedDiaQlty = prevDesign?.diaQlty || reorderMeta?.diamondQuality || "";
    const rawGoldQlty = prevDesign?.goldQlty || goldQlty || metaGoldQlty || "";
    const resolvedGoldQlty = goldQltyMap[String(rawGoldQlty).trim().toUpperCase()] || rawGoldQlty;
    const resolvedGoldColor = prevDesign?.goldColor || goldColor || metaGoldColor || "";
    const resolvedDeliveryDate = dayjs(deliveryDate || reorderMeta?.deliveryDate || new Date()).format("YYYY-MM-DD");
    const resolvedRhodiumLabel = rhodium || reorderMeta?.rhodium || "White";
    const resolvedRhodium = rhodiumMap[resolvedRhodiumLabel] || "W";
    const resolvedPriority = priority || reorderMeta?.priority || "Standard";

    const payload = {
      styleNumber: resolvedStyleNumber,
      diaQlty: resolvedDiaQlty,
      goldQlty: resolvedGoldQlty,
      goldColor: resolvedGoldColor,
      rhodium: resolvedRhodium,
      priority: resolvedPriority,
      deliveryDate: resolvedDeliveryDate,
      mode: mode || "",
      designs: [
        {
          styleNumber: resolvedStyleNumber,
          itemSize: baseDesign?.itemSize && baseDesign?.itemSize !== "N/A" ? baseDesign?.itemSize : baseDesign?.size || "",
          diaQlty: resolvedDiaQlty,
          goldQlty: resolvedGoldQlty,
          goldColor: resolvedGoldColor,
          rhodium: resolvedRhodium,
          priority: resolvedPriority,
          bufferStockQuantity: bufferQty || "",
          reOrderQuantity: reorderQty || "",
        },
      ],
    };

    try {
      setIsSubmitting(true);
      console.log("REORDER CREATE PAYLOAD:", payload);
      await apiClient.post(
        "/api/v3/client/vision_order/reorder/create",
        payload
      );
      const generatedPayload = {
        styleNumber: resolvedStyleNumber,
      };
      console.log("REORDER GENERATED PAYLOAD:", generatedPayload);

      try {
        await apiClient.post(
          "/api/v3/client/vision_order/reorder/genereted",
          generatedPayload
        );
      } catch (generatedError) {
        // Do not block successful reorder creation if generated endpoint fails.
        console.error("Reorder generated API failed:", generatedError?.response?.data || generatedError?.message);
      }

      toast.success("Order created successfully", { autoClose: 1200 });

      clearState();
      onGenerateSuccess?.();
    } catch (error) {
      console.error("Reorder create API failed:", error?.response?.data || error?.message);
      toast.error(error?.response?.data?.message || "Failed to create order", { autoClose: 2000 });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleLocationCardClick = (locationType) => {
    const filtered = reorderLocationCard.filter((item) =>
      item?.location?.toLowerCase().includes(locationType.toLowerCase())
    );

    if (!filtered.length) return;
 const title = `${locationType.toUpperCase()} - ${filtered.length} Pcs`;
     sessionStorage.setItem("reorderStockViewData", JSON.stringify(filtered));
    sessionStorage.setItem("reorderStockViewTitle", title);

    window.open(`/reorder-stock-view/${locationType}`);
  };


  useEffect(() => {
    if (!open) return;
    if (!resolvedStyleNumber) {
      setStocks([]);
      return;
    }

    let isActive = true;
    const fetchStocks = async () => {
      try {
        const res = await apiClient.get(`/api/v3/client/vision_order/reorder/getCurrentStocks/${resolvedStyleNumber}`);

        const allStyleData = res?.data?.checkStocks?.flatMap(item => item.styleData) || [];
        // const stockData = allStyleData?.map((item) => transformStockData(item));
        // console.log({ allStyleData });
        // console.log({ stockData });

        setReorderLocationCard(allStyleData)

        const stockRows = Array.isArray(res?.data?.checkStocks)
          ? res.data.checkStocks
          : Array.isArray(res?.data?.data)
            ? res.data.data
            : [];
        if (isActive) {
          setStocks(stockRows);
        }
      } catch (err) {
        if (isActive) {
          setStocks([]);
        }
      }
    };

    fetchStocks();
    return () => {
      isActive = false;
    };
  }, [open, resolvedStyleNumber]);

  const stockMap = React.useMemo(() => {
    if (!Array.isArray(stocks)) return { BBM: 0, BBS: 0 };

    const map = { BBM: 0, BBS: 0 };

    for (const item of stocks) {
      const loc = item?.location?.toLowerCase() || "";
      if (loc.includes("bbm")) map.BBM += Number(item.count);
      if (loc.includes("bbs")) map.BBS += Number(item.count);
    }

    return map;
  }, [stocks]);

  const reorderQtyLabel = toQtyLabel(reorderQty, "Re-order Qty");
  const bufferQtyLabel = toQtyLabel(bufferQty, "Buffer Qty");

  const getModeLabel = (mode) => {
    if (mode === "Generate Quote") return "Quote";
    if (mode === "Generate Order") return "Order";
    if (mode === "Generate Bag") return "Bag";
  };

  return (
    <Modal
      open={open}
      handleClose={clearState}
      style={{
        width: "min(460px, 92vw)",
        maxWidth: "92vw",
        mr: "clamp(16px, 30vw, 560px)",
        padding: "22px",
      }}
    >
      <Grid container spacing={2}>
        {/* Header */}
        <Grid item xs={12} className="reorder-header">
          <Typography
            style={{
              fontFamily: "Poppins",
              fontWeight: 500,
              fontSize: "24px",
              lineHeight: "100%",
              letterSpacing: "0%",
            }}
          >
            Re-Quote Settings
          </Typography>
          <IconButton onClick={clearState} size="small">
            <CloseIcon sx={{ fontSize: "18px", color: "#000 !important" }} />
          </IconButton>
        </Grid>

        {/* Current Stocks */}
        <Grid item xs={12}>
          <Typography
            style={{
              fontFamily: "Poppins",
              fontWeight: 500,
              fontSize: "18px",
              lineHeight: " 22px",
              letterSpacing: "-0.41px",
              paddingBottom: "20px",
            }}
          >
            Current Stocks
          </Typography>

          <div className="reorder-stock-row">
            <div
              className="reorder-stock-card"
              onClick={() => stockMap.BBM > 0 && handleLocationCardClick("BBM")}
              style={{ cursor: stockMap.BBM > 0 ? "pointer" : "default" }}
            >
              <Typography sx={{
                fontFamily: "Poppins", fontWeight: 400, fontSize: "24px",
                textDecoration: "underline", pb: "12px"
              }}>
                {stockMap.BBM} Pcs
              </Typography>
              <Typography sx={{
                fontFamily: "Poppins", fontWeight: 400, fontSize: "20px",
                lineHeight: "22px", letterSpacing: "-0.41px", textAlign: "center",
                color: "rgba(139, 139, 139, 1)"
              }}>
                BBM
              </Typography>
            </div>


            <div
              className="reorder-stock-card"
              onClick={() => stockMap.BBS > 0 && handleLocationCardClick("BBS")}
              style={{ cursor: stockMap.BBS > 0 ? "pointer" : "default" }}
            >
              <Typography sx={{ fontFamily: "Poppins", fontWeight: 400, fontSize: "24px", pb: "12px" }}>
                {stockMap.BBS} Pcs
              </Typography>
              <Typography sx={{
                fontFamily: "Poppins", fontWeight: 400, fontSize: "20px",
                lineHeight: "22px", letterSpacing: "-0.41px", textAlign: "center",
                color: "rgba(139, 139, 139, 1)"
              }}>
                BBS
              </Typography>
            </div>
          </div>
        </Grid>

        {/* Buffer Qty */}
        <Grid item xs={12} className="sco-main-grid-item">
          <div className="sco-label-row">
            <Typography className="sco-select-header-create">Buffer Stock Quantity</Typography>
          </div>
          <Selectinput
            value={bufferQty}
            onchange={(e) => setBufferQty(e.target.value)}
            options={BUFFER_OPTIONS}
            useMuiArrow={true}
            placeHolder="Select"
            centerOptions={true}
            customStyle={{
              ...styling,
              "& .MuiSelect-select": {
                color: bufferQty ? "#000" : "#9e9e9e",
                WebkitTextFillColor: bufferQty ? "#000" : "#9e9e9e",
              },
            }}
          />
        </Grid>

        {/* Reorder Qty */}
        <Grid item xs={12} className="sco-main-grid-item">
          <div className="sco-label-row">
            <Typography className="sco-select-header-create"> Re-Quote Quantity</Typography>
          </div>
          <Selectinput
            value={reorderQty}
            onchange={(e) => setReorderQty(e.target.value)}
            options={REORDER_OPTIONS}
            useMuiArrow={true}
            placeHolder="Select"
            centerOptions={true}
            customStyle={{
              ...styling,
              "& .MuiSelect-select": {
                color: reorderQty ? "#000" : "#9e9e9e",
                WebkitTextFillColor: reorderQty ? "#000" : "#9e9e9e",
              },
            }}
          />
        </Grid>

        {/* Mode */}
        <Grid item xs={12} className="sco-main-grid-item">
          <div className="sco-label-row">
            <Typography className="sco-select-header-create">Mode</Typography>
          </div>
          <Selectinput
            value={mode}
            onchange={(e) => setMode(e.target.value)}
            options={MODE_OPTIONS}
            useMuiArrow={true}
            placeHolder="Select"
            centerOptions={true}
            customStyle={{
              ...styling,
              "& .MuiSelect-select": {
                color: mode ? "#000" : "#9e9e9e",
                WebkitTextFillColor: mode ? "#000" : "#9e9e9e",
              },
            }}
          />
        </Grid>

        {/* Disclaimer */}
        <Grid item xs={12}>
          <div className="sco-label-row">
            <Typography
              style={{
                fontFamily: "Poppins",
                fontWeight: 500,
                fontSize: "16px",
                lineHeight: " 22px",
                letterSpacing: "-0.41px",
                textDecoration: "underline",
              }}
            >
              Disclaimer
            </Typography>
          </div>
          <div style={{ display: "flex", alignItems: "center", paddingTop: "6px" }}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={checked}
                  onChange={handleChange}
                  sx={{
                    color: "black",
                    "&.Mui-checked": {
                      color: "#50C878",
                    },
                  }}
                />
              }
              label={
                <Typography
                  sx={{
                    fontFamily: "Poppins",
                    fontWeight: 500,
                    fontSize: "14px",
                    lineHeight: "22px",
                    letterSpacing: "-0.41px",
                  }}
                >
                  {getModeLabel(mode)} will be generated automatically for [{reorderQtyLabel}] Pcs when the stock count goes below [{bufferQtyLabel}] Pcs.
                </Typography>
              }
            />
          </div>
        </Grid>

        {/* Button */}
        <Grid item xs={12}>
          <Button
            onClick={handleGenerateQuote}
            sx={{
              width: "100%",
              height: "54px",
              backgroundColor: canSave ? "#50C878" : "#444444",
              color: "#000",
              textTransform: "none",
              fontFamily: "Poppins",
              fontStyle: "Medium",
              fontSize: "18px",
              "&:hover": {
                backgroundColor: canSave ? "#50C878" : "#444444",
              },
            }}
          >
            {mode === "Generate Quote"
              ? "Generate Quote"
              : mode === "Generate Order"
                ? "Generate Order"
                : "Generate Bag"}
          </Button>
        </Grid>
      </Grid>
    </Modal>
  );
}

export default ReOrderSettingsModal;
